// Custom condition windows: user-defined rules evaluated by the backend
// (/api/combat/condition-windows, cmd/dilmeterapi/conditionWindows.go).
// Ported from mabinogi-dilmeter's summaryConfig.ts; the config JSON is the
// same, so configs exported from dilmeter import as-is.
import { ref } from 'vue';
import { ampCoverage, SkillAmpStats } from '@/skillAmp';

/**
 * targetHas / attackerHas  - the hit landed while the target / attacker had the condition.
 * whileTarget / whileAttacker - same test, and with "each entry" grouping every
 *   entry into that condition becomes its own window.
 */
export type ConditionFilterMode =
    | 'onHit_targetHas'
    | 'onHit_attackerHas'
    | 'duringCondition_target'
    | 'duringCondition_attacker';

export type ConditionFilterLeaf = { type: 'leaf'; mode: ConditionFilterMode; conditionId: number };
export type ConditionFilterGroup = { type: 'and' | 'or'; rules: ConditionFilter[] };
export type ConditionFilter = ConditionFilterLeaf | ConditionFilterGroup;

/** eachEntry - one window per entry into the condition (or per cluster of hits); none - one total. */
export type GroupByMode = 'eachEntry' | 'none';

export type CustomConditionConfig = {
    id: string;
    displayName: string;
    filter: ConditionFilter;
    groupBy: GroupByMode;
};

// ===== Results (from the backend) =====

export type ConditionHit = {
    at: number;
    skillId: number;
    damage: number;
    isCritical?: boolean;
    /** A passive proc's estimated trigger skill. */
    triggerSkillId?: number;
    /** Delayed hits (DoT ticks, most procs) can never crit. */
    isDelayed?: boolean;
    /** Dealt by the player's pet (not a marionette): shown apart, not in the player's crit rate. */
    fromPet?: boolean;
    /** Skill amplification (Config > Skill Amplification): checked, status keys up, all up. */
    ampChecked?: boolean;
    amp?: string[];
    ampAll?: boolean;
};

export type ConditionGroupPlayer = {
    entityId: string;
    name: string;
    totalDamage: number;
    passiveDamage: number;
    petDamage: number;
    critEligible: number;
    critHits: number;
    hits: ConditionHit[];
    /** Status uptime within the window, in the Analysis tab's Self Statuses shape. */
    statusStats: any[];
};

/**
 * windowSkillStats builds SkillBreakdown rows from one player's hits in a
 * window - the same row shape the Analysis tab builds from aggregated stats.
 * HPM is over the window's duration.
 */
export function windowSkillStats(
    hits: ConditionHit[],
    durationSec: number,
    skillName: (id: number) => string,
    passiveIds: Set<number>,
) {
    // Keyed by pet flag + skill id: a pet and its owner share ids (Combat Mastery).
    const bySkill = new Map<string, { id: number; pet: boolean;
        total: number; count: number; critable: number; crits: number; min: number; max: number;
        triggers: Map<number, { damage: number; count: number }>;
        amp?: SkillAmpStats;
    }>();
    let total = 0;
    for (const h of hits) {
        const key = `${h.fromPet ? 'pet' : 'own'}:${h.skillId}`;
        let s = bySkill.get(key);
        if (!s) {
            s = { id: h.skillId, pet: !!h.fromPet, total: 0, count: 0, critable: 0, crits: 0, min: Infinity, max: 0, triggers: new Map() };
            bySkill.set(key, s);
        }
        s.total += h.damage;
        s.count++;
        if (!h.isDelayed) {
            s.critable++;
            if (h.isCritical) s.crits++;
        }
        s.min = Math.min(s.min, h.damage);
        s.max = Math.max(s.max, h.damage);
        if (h.ampChecked) {
            const a = s.amp ?? (s.amp = { hits: 0, all: 0, any: 0, status: {} });
            a.hits++;
            if (h.ampAll) a.all++;
            if (h.amp?.length) a.any++;
            for (const k of h.amp ?? []) a.status[k] = (a.status[k] ?? 0) + 1;
        }
        if (h.triggerSkillId) {
            const t = s.triggers.get(h.triggerSkillId) ?? { damage: 0, count: 0 };
            t.damage += h.damage;
            t.count++;
            s.triggers.set(h.triggerSkillId, t);
        }
        total += h.damage;
    }

    const minutes = Math.max(durationSec, 1) / 60;
    const rows = [...bySkill.values()].map(s => ({
        id: s.id,
        name: skillName(s.id),
        total: Math.round(s.total),
        count: s.count,
        percent: total > 0 ? (s.total / total) * 100 : 0,
        critRate: s.critable > 0 ? (s.crits / s.critable) * 100 : 0,
        critable: s.critable > 0,
        hitsPerMinute: s.count / minutes,
        avg: s.count > 0 ? Math.round(s.total / s.count) : 0,
        min: Math.round(s.min),
        max: Math.round(s.max),
        barPercent: 0,
        passive: !s.pet && passiveIds.has(s.id),
        pet: s.pet,
        amp: s.pet ? undefined : ampCoverage(s.id, s.amp),
        triggers: [...s.triggers.entries()]
            .map(([tid, t]) => ({ id: tid, name: skillName(tid), damage: t.damage, count: t.count, percent: s.total > 0 ? (t.damage / s.total) * 100 : 0 }))
            .sort((a, b) => b.damage - a.damage),
    })).sort((a, b) => b.total - a.total);

    const top = rows[0]?.total ?? 0;
    const passiveDamage = rows.filter(r => r.passive).reduce((sum, r) => sum + r.total, 0);
    const petDamage = rows.filter(r => r.pet).reduce((sum, r) => sum + r.total, 0);
    const activeDamage = rows.reduce((sum, r) => sum + r.total, 0) - passiveDamage - petDamage;
    for (const r of rows) {
        r.barPercent = top > 0 ? (r.total / top) * 100 : 0;
    }
    return { skillStats: rows, activeDamage, passiveDamage, petDamage };
}

export type ConditionGroup = {
    index: number;
    startAt: number;
    endAt: number;
    duration: number;
    totalDamage: number;
    players: ConditionGroupPlayer[];
};

export type ConditionResult = {
    configId: string;
    displayName: string;
    groups: ConditionGroup[];
    totalDamage: number;
    averageDamage: number;
};

// ===== Storage =====

const STORAGE_KEY = 'funnycharts.conditionWindows.v1';

const DEFAULT_CONFIGS: CustomConditionConfig[] = [
    {
        id: 'cc_default_pall',
        displayName: 'Pall of Ruination',
        filter: { type: 'leaf', mode: 'duringCondition_target', conditionId: 803 },
        groupBy: 'eachEntry',
    },
];

const MODES: ConditionFilterMode[] = ['onHit_targetHas', 'onHit_attackerHas', 'duringCondition_target', 'duringCondition_attacker'];

function isFilter(f: unknown, depth = 0): f is ConditionFilter {
    if (!f || typeof f !== 'object' || depth > 8) return false;
    const x = f as Record<string, unknown>;
    if (x.type === 'leaf') return MODES.includes(x.mode as ConditionFilterMode) && Number.isInteger(x.conditionId);
    if (x.type === 'and' || x.type === 'or') return Array.isArray(x.rules) && x.rules.every(r => isFilter(r, depth + 1));
    return false;
}

/** parseConfigs validates stored or imported JSON entry by entry. */
export function parseConfigs(raw: string): CustomConditionConfig[] | null {
    let parsed: unknown;
    try {
        parsed = JSON.parse(raw);
    } catch {
        return null;
    }
    if (!Array.isArray(parsed)) return null;
    const out: CustomConditionConfig[] = [];
    for (const c of parsed) {
        if (!c || typeof c !== 'object' || !isFilter((c as any).filter)) continue;
        out.push({
            id: typeof c.id === 'string' && c.id ? c.id : newId(),
            displayName: typeof c.displayName === 'string' ? c.displayName : '',
            filter: c.filter,
            groupBy: c.groupBy === 'none' ? 'none' : 'eachEntry',
        });
    }
    return out;
}

function newId(): string {
    return `cc_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`;
}

function load(): CustomConditionConfig[] {
    try {
        const raw = localStorage.getItem(STORAGE_KEY);
        if (raw) {
            const parsed = parseConfigs(raw);
            if (parsed) return parsed;
        }
    } catch { /* storage unavailable */ }
    return DEFAULT_CONFIGS.map(c => ({ ...c }));
}

function save() {
    try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(conditionConfigs.value));
    } catch { /* storage unavailable: configs last for this page only */ }
}

export const conditionConfigs = ref<CustomConditionConfig[]>(load());

export function upsertCondition(config: Omit<CustomConditionConfig, 'id'> & { id?: string }) {
    const c = { ...config, id: config.id || newId() } as CustomConditionConfig;
    const i = conditionConfigs.value.findIndex(x => x.id === c.id);
    conditionConfigs.value = i >= 0
        ? conditionConfigs.value.map((x, j) => (j === i ? c : x))
        : [...conditionConfigs.value, c];
    save();
}

export function removeCondition(id: string) {
    conditionConfigs.value = conditionConfigs.value.filter(c => c.id !== id);
    save();
}

export function moveCondition(from: number, to: number) {
    const list = [...conditionConfigs.value];
    const [item] = list.splice(from, 1);
    list.splice(to, 0, item);
    conditionConfigs.value = list;
    save();
}

export function exportConditions(): string {
    return JSON.stringify(conditionConfigs.value, null, 2);
}

/** importConditions replaces every config; false when the JSON isn't a config list. */
export function importConditions(json: string): boolean {
    const parsed = parseConfigs(json);
    if (!parsed) return false;
    conditionConfigs.value = parsed;
    save();
    return true;
}

// ===== Display =====

export const MODE_LABELS: Record<ConditionFilterMode, string> = {
    onHit_targetHas: 'Target has',
    onHit_attackerHas: 'Attacker has',
    duringCondition_target: 'While target has',
    duringCondition_attacker: 'While attacker has',
};

export function describeFilter(filter: ConditionFilter, condNameMap: Record<number, string>): string {
    if (filter.type === 'leaf') {
        return `${MODE_LABELS[filter.mode]} "${condNameMap[filter.conditionId] ?? `#${filter.conditionId}`}"`;
    }
    const parts = filter.rules.map(r => describeFilter(r, condNameMap));
    return parts.length === 1 ? parts[0] : `(${parts.join(filter.type === 'and' ? ' and ' : ' or ')})`;
}
